import { create } from "zustand";
import type { HomeSectionId } from "../content/siteContent";
import { clamp01, sectionEntryConfigs, type SectionEntryPhase } from "../lib/sectionEntry";

export type SectionEntryState = {
  activatedAt: number | null;
  activationRawProgress: number;
  browseProgress: number;
  browseStartRawProgress: number | null;
  canAdvance: boolean;
  direction: "forward" | "backward";
  entryProgress: number;
  holdProgress: number;
  isEntryLocked: boolean;
  phase: SectionEntryPhase;
  rawProgress: number;
  unlocked: boolean;
};

type SectionEntryStore = {
  activeSection: HomeSectionId | null;
  scrollVelocity: number;
  sections: Record<HomeSectionId, SectionEntryState>;
  activateSection: (id: HomeSectionId, now: number, rawProgress: number, direction?: "forward" | "backward") => void;
  markSectionExit: (id: HomeSectionId) => void;
  setScrollVelocity: (velocity: number) => void;
  updateActiveSectionProgress: (id: HomeSectionId, rawProgress: number, velocity: number, now: number, isTransitioning: boolean) => void;
};

const defaultState = (): SectionEntryState => ({
  activatedAt: null,
  activationRawProgress: 0,
  browseProgress: 0,
  browseStartRawProgress: null,
  canAdvance: true,
  direction: "forward",
  entryProgress: 1,
  holdProgress: 1,
  isEntryLocked: false,
  phase: "browse",
  rawProgress: 0,
  unlocked: true
});

const createSectionStates = (): Record<HomeSectionId, SectionEntryState> => ({
  studio: defaultState(),
  projects: defaultState(),
  team: defaultState(),
  about: defaultState(),
  contact: defaultState()
});

function computeLockedState(
  id: HomeSectionId,
  previous: SectionEntryState,
  rawProgress: number,
  velocity: number,
  now: number,
  isTransitioning: boolean
): SectionEntryState {
  const config = sectionEntryConfigs[id];
  const clampedProgress = clamp01(rawProgress);

  if (!config) {
    return {
      ...previous,
      browseProgress: clampedProgress,
      browseStartRawProgress: clampedProgress,
      canAdvance: !isTransitioning,
      entryProgress: 1,
      holdProgress: 1,
      isEntryLocked: false,
      phase: isTransitioning ? "exit" : "browse",
      rawProgress: clampedProgress,
      unlocked: true
    };
  }

  if (isTransitioning) {
    return {
      ...previous,
      canAdvance: false,
      phase: "exit",
      rawProgress: clampedProgress
    };
  }

  const activatedAt = previous.activatedAt ?? now;
  const enterDurationMs = Math.max(config.holdDurationMs * config.enterRatio, 1);
  const holdElapsed = Math.max(now - activatedAt, 0);
  const entryProgress = clamp01(holdElapsed / enterDurationMs);
  const holdProgress =
    config.holdDurationMs <= enterDurationMs
      ? 1
      : clamp01((holdElapsed - enterDurationMs) / (config.holdDurationMs - enterDurationMs));
  const activationTravel =
    previous.direction === "backward"
      ? Math.max(previous.activationRawProgress - clampedProgress, 0)
      : Math.max(clampedProgress - previous.activationRawProgress, 0);

  let unlocked = previous.unlocked;
  let browseStartRawProgress = previous.browseStartRawProgress;
  if (!unlocked) {
    const holdComplete = holdElapsed >= config.holdDurationMs;
    const velocitySafe = Math.abs(velocity) <= config.velocityThreshold;
    const deadZonePassed = activationTravel >= config.deadZone;
    if (holdComplete && (velocitySafe || deadZonePassed)) {
      unlocked = true;
      browseStartRawProgress = clampedProgress;
    }
  }

  let browseProgress = previous.browseProgress;
  if (unlocked && browseStartRawProgress !== null) {
    if (previous.direction === "backward") {
      const browseRange = Math.max(browseStartRawProgress, 0.0001);
      const browseDistance = Math.max(browseStartRawProgress - clampedProgress, 0);
      const nextBrowseProgress = 1 - clamp01(browseDistance / browseRange);
      browseProgress = Math.min(previous.browseProgress, nextBrowseProgress);
    } else {
      const browseRange = Math.max(1 - browseStartRawProgress, 0.0001);
      const browseDistance = Math.max(clampedProgress - browseStartRawProgress, 0);
      const nextBrowseProgress = clamp01(browseDistance / browseRange);
      browseProgress = Math.max(previous.browseProgress, nextBrowseProgress);
    }
  }
  const phase: SectionEntryPhase = unlocked ? "browse" : entryProgress < 1 ? "enter" : "hold";

  return {
    activatedAt,
    activationRawProgress: previous.activationRawProgress,
    browseProgress,
    browseStartRawProgress,
    canAdvance: unlocked && !isTransitioning,
    direction: previous.direction,
    entryProgress,
    holdProgress,
    isEntryLocked: !unlocked,
    phase,
    rawProgress: clampedProgress,
    unlocked
  };
}

export const useSectionEntryStore = create<SectionEntryStore>((set) => ({
  activeSection: null,
  scrollVelocity: 0,
  sections: createSectionStates(),
  activateSection: (id, now, rawProgress, direction = "forward") =>
    set((state) => {
      if (state.activeSection === id && state.sections[id].activatedAt !== null && state.sections[id].phase !== "exit") {
        return state;
      }

      const existing = state.sections[id];
      const config = sectionEntryConfigs[id];
      const clampedProgress = clamp01(rawProgress);
      const nextSection = config
        ? {
            activatedAt: direction === "backward" ? now - config.holdDurationMs : now,
            activationRawProgress: clampedProgress,
            browseProgress: direction === "backward" ? 1 : 0,
            browseStartRawProgress: null,
            canAdvance: false,
            direction,
            entryProgress: direction === "backward" ? 1 : 0,
            holdProgress: direction === "backward" ? 1 : 0,
            isEntryLocked: true,
            phase: direction === "backward" ? ("hold" as const) : ("enter" as const),
            rawProgress: clampedProgress,
            unlocked: false
          }
        : {
            ...existing,
            activatedAt: now,
            activationRawProgress: clampedProgress,
            browseProgress: clampedProgress,
            browseStartRawProgress: clampedProgress,
            canAdvance: true,
            direction,
            entryProgress: 1,
            holdProgress: 1,
            isEntryLocked: false,
            phase: "browse" as const,
            rawProgress: clampedProgress,
            unlocked: true
          };

      return {
        activeSection: id,
        sections: {
          ...state.sections,
          [id]: nextSection
        }
      };
    }),
  markSectionExit: (id) =>
    set((state) => ({
      sections: {
        ...state.sections,
        [id]: {
          ...state.sections[id],
          canAdvance: false,
          phase: "exit"
        }
      }
    })),
  setScrollVelocity: (scrollVelocity) => set({ scrollVelocity }),
  updateActiveSectionProgress: (id, rawProgress, velocity, now, isTransitioning) =>
    set((state) => ({
      sections: {
        ...state.sections,
        [id]: computeLockedState(id, state.sections[id], rawProgress, velocity, now, isTransitioning)
      }
    }))
}));
